import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SocketProvider } from './context/SocketContext';
import API from './utils/api';

// Layout
import Nav    from './components/layout/Nav';
import Footer from './components/layout/Footer';

// Pages
import { HomePage, LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import BrowsePage         from './pages/BrowsePage';
import ListingDetailPage  from './pages/ListingDetailPage';
import ListItemPage       from './pages/ListItemPage';
import EditListingPage    from './pages/EditListingPage';
import CheckoutPage       from './pages/CheckoutPage';
import DashboardPage      from './pages/DashboardPage';
import OrderDetailPage    from './pages/OrderDetailPage';
import TrackOrderPage     from './pages/TrackOrderPage';
import RentalStatusPage   from './pages/RentalStatusPage';
import BarterBrowsePage   from './pages/BarterBrowsePage';
import BarterDetailPage from './pages/BarterDetailPage';
import CreateBarterListingPage from './pages/CreateBarterListingPage';
import FreeBrowsePage from './pages/FreeBrowsePage';
import FreeDetailPage from './pages/FreeDetailPage';
import CreateFreeListingPage from './pages/CreateFreeListingPage';
import UserProfilePage from './pages/UserProfilePage';
import SubscriptionPage from './pages/SubscriptionPage';
import WatchlistPage from './pages/WatchlistPage';
import ISOPage from './pages/ISOPage';
import NotFoundPage from './pages/NotFoundPage';
import LiveBrowsePage from './pages/LiveBrowsePage';
import LivePage from './pages/LivePage';
import LiveWatchPage from './pages/LiveWatchPage';
import { MessagesPage, SettingsPage, AdminPage, OrdersPage, BookingsPage, RatingsPage, ResponseTimePage, SalesPage, ReviewsPage, MyReviewsPage } from './pages/OtherPages';
import {
  TermsPage, PrivacyPage, CookiesPage, RefundPage,
  AcceptableUsePage, CommunityPage, DmcaPage,
  OwnerAgreementPage, TrustSafetyPage,
} from './pages/LegalPages';

// CSS
import './styles/global.css';

// ── Error Boundary ────────────────────────────────────────────────
import { Component } from 'react';
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App error:', error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🍒</div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Something went wrong</h1>
          <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 28, lineHeight: 1.6 }}>
            An unexpected error occurred. Please refresh the page or go back home.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn btn-cherry" onClick={() => window.location.href = '/'}>Go home</button>
            <button className="btn btn-ghost" onClick={() => window.location.reload()}>Refresh</button>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <pre style={{ marginTop: 24, padding: 16, background: '#fff', borderRadius: 8, border: '1px solid var(--border)', fontSize: 11, textAlign: 'left', overflow: 'auto', color: 'var(--cherry)' }}>
              {this.state.error?.toString()}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

// ── Protected route ───────────────────────────────────────────────
function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 48 }} className="float-anim">🍒</div>
      <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading...</div>
    </div>
  );

  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  if (adminOnly && !['admin', 'superadmin'].includes(user.role)) return <Navigate to="/" replace />;

  return children;
}

// ── Site status (maintenance + announcement) ──────────────────────
function SiteStatusProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    API.get('/site-status').then(r => setStatus(r.data)).catch(() => {});
    const iv = setInterval(() => API.get('/site-status').then(r => setStatus(r.data)).catch(() => {}), 60000);
    return () => clearInterval(iv);
  }, []);

  const isAdmin = user && ['admin','superadmin'].includes(user.role);

  if (status?.maintenanceMode && !isAdmin) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#FAFAFA', gap:16, padding:24, textAlign:'center' }}>
        <div style={{ fontSize:64 }}>🔧</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700 }}>Down for Maintenance</h1>
        <p style={{ color:'var(--text-soft)', maxWidth:400 }}>{status.maintenanceMessage}</p>
      </div>
    );
  }

  const bannerColors = { info:'#EBF0FF', success:'#EAF5EC', warning:'#FFF9E6', error:'#FDECEA' };
  const bannerText   = { info:'#1A5276', success:'#2D7A3A', warning:'#C9930A', error:'var(--cherry)' };

  return (
    <>
      {status?.announcement && (
        <div style={{ background: bannerColors[status.announcementType]||bannerColors.info, color: bannerText[status.announcementType]||bannerText.info, textAlign:'center', padding:'10px 16px', fontSize:14, fontWeight:600 }}>
          {status.announcement}
        </div>
      )}
      {children}
    </>
  );
}

// ── Layout wrapper ────────────────────────────────────────────────
function AdminBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  if (!user || !['admin', 'superadmin'].includes(user.role)) return null;
  if (location.pathname === '/admin') return null;
  return (
    <div style={{ background:'#1A1A2E', color:'white', fontSize:12, padding:'6px 20px', display:'flex', alignItems:'center', gap:12 }}>
      <span style={{ opacity:.6 }}>🛡️ Admin</span>
      <button onClick={() => navigate('/admin')}
        style={{ background:'rgba(255,255,255,.12)', border:'none', color:'white', fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:100, cursor:'pointer' }}>
        ← Back to Admin Panel
      </button>
      <span style={{ opacity:.4, marginLeft:4 }}>{location.pathname}</span>
    </div>
  );
}

function Layout({ children }) {
  return (
    <>
      <Nav />
      <AdminBar />
      <main className="page-wrapper">{children}</main>
      <Footer />
    </>
  );
}

// ── No-footer pages (messages has its own layout) ─────────────────
function ChatLayout({ children }) {
  return (
    <>
      <Nav />
      <main>{children}</main>
    </>
  );
}

// ── Simple placeholder ────────────────────────────────────────────
function Placeholder({ title, icon = '🍒', desc = 'This page is coming soon.' }) {
  return (
    <div style={{ textAlign: 'center', padding: '100px 40px', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: 56, marginBottom: 20 }} className="float-anim">{icon}</div>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 30, fontWeight: 700, marginBottom: 12 }}>{title}</h1>
      <p style={{ fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.7, marginBottom: 24 }}>{desc}</p>
      <div style={{ display: 'inline-flex', gap: 8, padding: '10px 20px', background: 'var(--cherry-light)', borderRadius: 100, fontSize: 13, fontWeight: 600, color: 'var(--cherry)' }}>
        🚀 Coming soon
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/browse" element={<Layout><BrowsePage /></Layout>} />
      <Route path="/listing/:id" element={<Layout><ListingDetailPage /></Layout>} />
      <Route path="/profile/:id" element={<Layout><UserProfilePage /></Layout>} />
      <Route path="/barter" element={<Layout><BarterBrowsePage /></Layout>} />
      <Route path="/barter/:id" element={<Layout><BarterDetailPage /></Layout>} />
      <Route path="/free" element={<Layout><FreeBrowsePage /></Layout>} />
      <Route path="/free/:id" element={<Layout><FreeDetailPage /></Layout>} />
      <Route path="/looking-for" element={<Layout><ISOPage /></Layout>} />
      <Route path="/live" element={<Layout><LiveBrowsePage /></Layout>} />
      <Route path="/live/go" element={<Protected><LivePage /></Protected>} />
      <Route path="/live/:roomName" element={<LiveWatchPage />} />
      <Route path="/how-it-works" element={<Layout><Placeholder title="How it Works" icon="💡" desc="Everything you need to know about renting, bidding, buying and selling on Cherrypkd." /></Layout>} />

      {/* Auth */}
      <Route path="/login"            element={<Layout><LoginPage /></Layout>} />
      <Route path="/signup"           element={<Layout><SignupPage /></Layout>} />
      <Route path="/forgot-password"  element={<Layout><ForgotPasswordPage /></Layout>} />
      <Route path="/reset-password"   element={<Layout><ResetPasswordPage /></Layout>} />
      <Route path="/verify-email"     element={<Layout><Placeholder title="Email Verified" icon="✅" desc="Your email has been verified. You can now sign in." /></Layout>} />

      {/* Protected */}
      <Route path="/list" element={<Protected><Layout><ListItemPage /></Layout></Protected>} />
      <Route path="/listing/:id/edit" element={<Protected><Layout><EditListingPage /></Layout></Protected>} />
      <Route path="/barter/create" element={<Protected><Layout><CreateBarterListingPage /></Layout></Protected>} />
      <Route path="/free/create" element={<Protected><Layout><CreateFreeListingPage /></Layout></Protected>} />
      <Route path="/checkout" element={<Layout><CheckoutPage /></Layout>} />
      <Route path="/track" element={<Layout><TrackOrderPage /></Layout>} />
      <Route path="/orders/:id" element={<Protected><Layout><OrderDetailPage /></Layout></Protected>} />
      <Route path="/rentals/:id" element={<Protected><Layout><RentalStatusPage /></Layout></Protected>} />

      {/* Dashboard routes */}
      <Route path="/dashboard"                  element={<Protected><Layout><DashboardPage /></Layout></Protected>} />
      <Route path="/dashboard/:tab"             element={<Protected><Layout><DashboardPage /></Layout></Protected>} />
      <Route path="/dashboard/watchlist"        element={<Protected><Layout><WatchlistPage /></Layout></Protected>} />
      <Route path="/dashboard/subscription"     element={<Protected><Layout><SubscriptionPage /></Layout></Protected>} />
      <Route path="/dashboard/orders"           element={<Protected><Layout><OrdersPage /></Layout></Protected>} />
      <Route path="/dashboard/bookings"         element={<Protected><Layout><BookingsPage /></Layout></Protected>} />
      <Route path="/dashboard/ratings"          element={<Protected><Layout><RatingsPage /></Layout></Protected>} />
      <Route path="/dashboard/response-time"    element={<Protected><Layout><ResponseTimePage /></Layout></Protected>} />
      <Route path="/dashboard/sales"            element={<Protected><Layout><SalesPage /></Layout></Protected>} />
      <Route path="/dashboard/reviews"          element={<Protected><Layout><ReviewsPage /></Layout></Protected>} />
      <Route path="/dashboard/my-reviews"       element={<Protected><Layout><MyReviewsPage /></Layout></Protected>} />

      {/* Messages */}
      <Route path="/messages"              element={<Protected><ChatLayout><MessagesPage /></ChatLayout></Protected>} />
      <Route path="/messages/:conversationId" element={<Protected><ChatLayout><MessagesPage /></ChatLayout></Protected>} />

      {/* Settings */}
      <Route path="/settings" element={<Protected><Layout><SettingsPage /></Layout></Protected>} />

      {/* Admin */}
      <Route path="/admin" element={<Protected adminOnly><Layout><AdminPage /></Layout></Protected>} />

      {/* Info pages */}
      <Route path="/categories"    element={<Layout><Placeholder title="All Categories" icon="📂" desc="Browse all 24 categories on Cherrypkd." /></Layout>} />
      {/* Legal pages */}
      <Route path="/terms"            element={<Layout><TermsPage /></Layout>} />
      <Route path="/privacy"          element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/cookies"          element={<Layout><CookiesPage /></Layout>} />
      <Route path="/refund"           element={<Layout><RefundPage /></Layout>} />
      <Route path="/acceptable-use"   element={<Layout><AcceptableUsePage /></Layout>} />
      <Route path="/community"        element={<Layout><CommunityPage /></Layout>} />
      <Route path="/dmca"             element={<Layout><DmcaPage /></Layout>} />
      <Route path="/owner-agreement"  element={<Layout><OwnerAgreementPage /></Layout>} />
      <Route path="/trust"            element={<Layout><TrustSafetyPage /></Layout>} />

      {/* 404 */}
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <SiteStatusProvider>
        <CartProvider>
          <SocketProvider>
          <AppRoutes />
          <Toaster
            position="bottom-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#1A1A1A',
                color: '#fff',
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 100,
                padding: '12px 20px',
              },
            }}
          />
          </SocketProvider>
        </CartProvider>
        </SiteStatusProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
